// Choosing the delivery promise.
//
// A surcharge that appears on an invoice a clinic never agreed to is how a lab loses a customer, so
// the price sits on the option itself and, once Express is chosen, is repeated as a plain statement
// underneath. Nobody picks Express here without having been told what it costs.
import { formatDate, TURNAROUNDS, turnaroundDueDate, turnaroundOf, type WfPriority } from '@alignova/shared';

export function TurnaroundPicker({
  value,
  onChange,
  receivedAt,
  disabled,
  className = '',
}: {
  value: WfPriority;
  onChange: (v: WfPriority) => void;
  /** When the lab has (or will have) the records — what the promised date is counted from. */
  receivedAt?: string | null;
  disabled?: boolean;
  className?: string;
}) {
  const tier = turnaroundOf(value);
  const due = turnaroundDueDate(receivedAt ?? new Date().toISOString(), value);

  return (
    <fieldset className={className} disabled={disabled}>
      <legend className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted">Delivery</legend>

      <div className="grid gap-2 sm:grid-cols-2">
        {TURNAROUNDS.map((t) => {
          const on = value === t.key;
          return (
            <label
              key={t.key}
              className={`tap flex cursor-pointer flex-col gap-0.5 rounded-lg border p-3 transition ${
                on ? 'border-accent bg-accent/10 shadow-level-1' : 'border-line bg-surface hover:border-accent/40'
              }`}
            >
              <span className="flex items-center gap-2">
                <input
                  type="radio"
                  name="turnaround"
                  className="accent-[var(--c-accent)]"
                  checked={on}
                  onChange={() => onChange(t.key)}
                />
                <span className="font-semibold text-ink">{t.label}</span>
              </span>
              <span className="tabnum pl-6 text-sm text-muted">{t.days} days</span>
              <span className={`pl-6 text-xs font-medium ${t.surchargeRupees ? 'text-status-warn-fg' : 'text-muted'}`}>
                {t.surchargeRupees ? `+ PKR ${t.surchargeRupees.toLocaleString('en-PK')}` : 'No extra charge'}
              </span>
            </label>
          );
        })}
      </div>

      {tier.surchargeRupees > 0 && (
        <p className="mt-2 rounded-md border border-status-warn-fg/30 bg-status-warn-bg/60 px-3 py-2 text-sm text-status-warn-fg">
          <b>{tier.label} delivery selected.</b> This case will be ready in {tier.days} days, and an
          extra <b>PKR {tier.surchargeRupees.toLocaleString('en-PK')}</b> will be charged on top of
          the aligner price.
        </p>
      )}

      <p className="mt-1.5 text-xs text-muted">
        Expected delivery <b className="text-ink">{formatDate(due)}</b> - counted from when the lab
        has your records.
      </p>
    </fieldset>
  );
}
